import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import { useExportFields } from '../../../../hooks/QueryBuilder';

interface QueryExportDialogProps {
    open: boolean;
    defaultEmail?: string;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onConfirm: (params: { notify: string[]; fields: string[] }) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeForMatch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// Mirrors the static "Civilité / Nom / Prénom / Numéro de carte de fidélité" from
// requiredFieldsList: those fields are always sent with the export and can't be toggled, but the
// API's exportable-fields list includes them too — filtered out here so they aren't offered (and
// checked) a second time as "optional".
const REQUIRED_FIELD_LABELS = new Set([
    'civilite',
    'nom',
    'nom du client',
    'prenom',
    'prenom du client',
    'numero de carte de fidelite',
]);

const isRequiredField = (label: string): boolean => REQUIRED_FIELD_LABELS.has(normalizeForMatch(label));

const parseEmails = (value: string) => (
    value
        .split(/[\n,;]+/)
        .map(email => email.trim())
        .filter(Boolean)
);

function QueryExportDialog({
    open,
    defaultEmail = '',
    loading = false,
    error,
    onClose,
    onConfirm,
}: QueryExportDialogProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { fields: allExportFields, loading: fieldsLoading, error: fieldsError } = useExportFields();
    const exportFields = useMemo(
        () => allExportFields.filter(field => !isRequiredField(field.label)),
        [allExportFields]
    );
    const [emailText, setEmailText] = useState(defaultEmail);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setEmailText(defaultEmail);
        setSelectedFields([]);
        setValidationError(null);
    }, [open, defaultEmail]);

    const emails = useMemo(() => parseEmails(emailText), [emailText]);

    const toggleField = (code: string) => {
        setSelectedFields(current => (
            current.includes(code)
                ? current.filter(f => f !== code)
                : [...current, code]
        ));
    };

    const handleConfirm = () => {
        if (emails.length === 0) {
            setValidationError(t('pages.clients.queryBuilder.exportDialog.emailRequired'));
            return;
        }

        const invalidEmail = emails.find(email => !EMAIL_PATTERN.test(email));
        if (invalidEmail) {
            setValidationError(t('pages.clients.queryBuilder.exportDialog.invalidEmail', { email: invalidEmail }));
            return;
        }

        setValidationError(null);
        onConfirm({ notify: emails, fields: selectedFields });
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {t('pages.clients.queryBuilder.exportDialog.title')}
                <IconButton onClick={onClose} size="small" disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info">
                        {t('pages.clients.queryBuilder.exportDialog.infoMessage')}
                    </Alert>

                    {(error || validationError) && (
                        <Alert severity="error">
                            {validationError || error}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t('pages.clients.queryBuilder.exportDialog.requiredFieldsTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('pages.clients.queryBuilder.exportDialog.requiredFieldsList')}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t('pages.clients.queryBuilder.exportDialog.optionalFieldsTitle')}
                        </Typography>
                        {fieldsLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('pages.clients.queryBuilder.exportDialog.fieldsLoading')}
                                </Typography>
                            </Box>
                        )}
                        {!fieldsLoading && fieldsError && (
                            <Typography variant="body2" color="error">
                                {t('pages.clients.queryBuilder.exportDialog.fieldsError')}
                            </Typography>
                        )}
                        {!fieldsLoading && !fieldsError && exportFields.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {t('pages.clients.queryBuilder.exportDialog.noOptionalFields')}
                            </Typography>
                        )}
                        {!fieldsLoading && !fieldsError && exportFields.length > 0 && (
                            <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, columnGap: 1 }}>
                                {exportFields.map(field => (
                                    <FormControlLabel
                                        key={field.code}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={selectedFields.includes(field.code)}
                                                onChange={() => toggleField(field.code)}
                                                disabled={loading}
                                            />
                                        )}
                                        label={field.label}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t('pages.clients.queryBuilder.exportDialog.emailLabel')}
                        </Typography>
                        <Typography variant="body2">
                            {emailText}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('pages.clients.queryBuilder.exportDialog.emailHelper')}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    {t('pages.clients.queryBuilder.exportDialog.back')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {loading ? t('pages.clients.queryBuilder.exportDialog.submitting') : t('pages.clients.queryBuilder.exportDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QueryExportDialog;
